import React, { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";

import Footer from "../../components/Footer";
import DropdownMenu from "../../components/DropdownMenu";
import SEO from "../../context/SEO";
import FAQ from "../../components/FAQ";
import { ShopContext } from "../../context/ShopContext";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import ScrollSmoother from "gsap/ScrollSmoother";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import "./style.css";
import styles from "./style.module.scss";
import newShopVideo from "../../assets/videos/heroBackgroundFinal.mp4";
import mobileHomepageVideo from "../../assets/videos/mobilehompageVideo.mp4";
import greenBowlBooks from "../../assets/videos/productVideos/greenBowlBooks.mp4";
import teracottaLampHands from "../../assets/videos/orbSTerracottaShorter.mp4";
import OrbS1 from "../../assets/home/OrbS1.avif";
import OrbS2 from "../../assets/home/OrbS2.avif";
import OrbS4 from "../../assets/home/OrbS4.avif";
import Duo2 from "../../assets/home/Duo2.avif";
import Duo3 from "../../assets/home/Duo3.avif";
import terracottaOrbs from "../../assets/home/orbTerracottaPlant.avif";
import trayNaturalStool from "../../assets/trayNaturalStool.avif";
import verticalAmbient from "../../assets/verticalAmbient.avif";
import orbShelf from "../../assets/home/orbShelf.avif";
import houses from "../../assets/home/bannerOrbs.avif";
import shopPhoto from "../../assets/shop/shopNight.avif";
import togoHouses from "../../assets/home/togoHouses.avif";
import OrbXLNat from "../../assets/home/OrbXLNat.avif";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, SplitText);

function Home() {
  const isSafari =
    /^((?!chrome|crios|android|edg|opr|firefox).)*safari/i.test(
      navigator.userAgent,
    ) &&
    navigator.vendor &&
    navigator.vendor.includes("Apple");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 500);
  // Hero video is chosen by ORIENTATION (not width) so a portrait iPad gets the
  // tall/vertical clip and a landscape one gets the wide clip.
  const [isPortrait, setIsPortrait] = useState(
    () => window.matchMedia("(orientation: portrait)").matches,
  );
  const [showElements, setShowElements] = useState("fade-in-down");
  const [opacity, setOpacity] = useState(1);
  const [fadeDirection, setFadeDirection] = useState("in");
  const [visible, setVisible] = useState(false);
  const scrollInitiated = useRef(false);
  const [vh, setVh] = useState(window.innerHeight * 0.01);
  const { products } = useContext(ShopContext);

  const prevScrollPos = useRef(window.scrollY);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 500;
      setIsMobile(mobile);
      console.log(
        `isMobile set to: ${mobile}, window.innerWidth: ${window.innerWidth}`,
      );
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const updateVh = () => setVh(window.innerHeight * 0.01);
    updateVh();
    window.addEventListener("resize", updateVh);
    return () => window.removeEventListener("resize", updateVh);
  }, []);

  // Keep the hero video in sync with device orientation (rotating an iPad, etc.)
  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait)");
    const update = () => setIsPortrait(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const scrollingUp = currentScrollPos < prevScrollPos.current;
      const scrollingDown = currentScrollPos > prevScrollPos.current;
      const atTop = currentScrollPos < 10 * vh;

      // First-time intro animation (your existing logo fade-in/out)
      if (!scrollInitiated.current) {
        setTimeout(() => setVisible(true), 2000);
        setTimeout(() => setVisible(false), 6000);
        scrollInitiated.current = true;
      }

      // Dropdown menu behavior — THIS IS THE KEY
      if (atTop) {
        setShowElements("full-opacity"); // ← No jump at top
      } else if (scrollingUp) {
        setShowElements("fade-in-up"); // Show when scrolling up
      } else if (scrollingDown) {
        setShowElements("fade-out-up"); // Hide when scrolling down
      }

      // Video fade-out (keep your existing logic)
      const startFadeOut = 20 * vh;
      const endFadeOut = 70 * vh;
      if (currentScrollPos < startFadeOut) {
        setOpacity(1);
      } else if (
        currentScrollPos >= startFadeOut &&
        currentScrollPos <= endFadeOut
      ) {
        const opacity =
          1 - (currentScrollPos - startFadeOut) / (endFadeOut - startFadeOut);
        setOpacity(opacity);
      } else {
        setOpacity(0);
      }

      prevScrollPos.current = currentScrollPos;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [vh]); // ← Only re-runs if vh changes (mobile rotate, etc.)

  const textSection1 = useRef(null);
  const image1 = useRef(null);
  const textSection2 = useRef(null);
  const image2 = useRef(null);
  const textSection3 = useRef(null);
  const image3 = useRef(null);
  const textSection4 = useRef(null);
  const section4 = useRef(null);
  const textSection5 = useRef(null);
  const section5 = useRef(null);
  const videoContainerTrayRef = useRef(null);
  const videoRefTray = useRef(null);
  const text1Ref = useRef(null);
  const text2Ref = useRef(null);
  const imageSectionRef = useRef(null);
  const videoDurationTrayRef = useRef(0);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const video = videoRefTray.current;
    if (video) {
      const onMetadataLoaded = () => {
        videoDurationTrayRef.current = video.duration || 10;
        setVideoReady(true);
      };
      video.addEventListener("loadedmetadata", onMetadataLoaded);
      return () => {
        video.removeEventListener("loadedmetadata", onMetadataLoaded);
      };
    }
  }, []);

  useGSAP(() => {
    // Existing animation for textSection1
    gsap.from(textSection1.current.children, {
      y: 50,
      opacity: 0,
      stagger: 0.25,
      ease: "power2.out",
      duration: 1,
      delay: 0.4,
      scrollTrigger: {
        trigger: textSection1.current,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    // Existing animation for image1
    gsap.from(image1.current, {
      x: 50,
      opacity: 0,
      stagger: 0.12,
      ease: "power2.out",
      duration: 1,
      delay: 0.4,
      scrollTrigger: {
        trigger: image1.current,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    gsap.to(image1.current.querySelector("img"), {
      y: -100,
      ease: "none",
      scrollTrigger: {
        trigger: image1.current,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
      },
    });

    // Existing animation for image2
    gsap.from(image2.current, {
      x: 50,
      opacity: 0,
      stagger: 0.12,
      ease: "power2.out",
      duration: 1,
      delay: 0.4,
      scrollTrigger: {
        trigger: image2.current,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    // Existing animation for image3
    gsap.from(image3.current, {
      x: 50,
      opacity: 0,
      stagger: 0.12,
      ease: "power2.out",
      duration: 1,
      delay: 0.4,
      scrollTrigger: {
        trigger: image3.current,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    // New staggered animation for imageSectionRef children
    gsap.from(imageSectionRef.current.children, {
      x: -50,
      opacity: 0,
      stagger: 0.2,
      ease: "power2.out",
      duration: 1,
      delay: 0.4,
      scrollTrigger: {
        trigger: imageSectionRef.current,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    // Existing animation for section4
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section4.current,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    tl.from(section4.current.children, {
      x: 50,
      opacity: 0,
      stagger: 0.5,
      ease: "power2.out",
      duration: 1,
    });

    tl.from(
      textSection4.current.children,
      {
        y: 50,
        opacity: 0,
        stagger: 0.25,
        ease: "power2.out",
        duration: 1,
      },
      "-=1.5",
    );
  });

  useGSAP(() => {
    const videoTray = videoRefTray.current;
    if (videoTray && videoReady && videoDurationTrayRef.current > 0) {
      const videoTl = gsap.timeline({ paused: true });

      videoTl.fromTo(
        text1Ref.current,
        { opacity: 0, y: "10vh" },
        { opacity: 1, y: "0", duration: 1, ease: "power2.out" },
        0,
      );
      videoTl.to(
        text1Ref.current,
        { y: "-10vh", duration: 1, ease: "none" },
        1,
      );
      videoTl.to(
        text1Ref.current,
        { opacity: 0, duration: 1, ease: "power2.in" },
        2,
      );

      videoTl.fromTo(
        text2Ref.current,
        { opacity: 0, y: "10vh" },
        { opacity: 1, y: "0", duration: 1, ease: "power2.out" },
        3,
      );
      videoTl.to(
        text2Ref.current,
        { y: "-10vh", duration: 1, ease: "none" },
        4,
      );
      videoTl.to(
        text2Ref.current,
        { opacity: 0, duration: 1, ease: "power2.in" },
        5,
      );

      videoTl.fromTo(
        videoTray,
        { currentTime: 0 },
        {
          currentTime: videoDurationTrayRef.current,
          duration: videoTl.duration(),
          ease: "none",
        },
        0,
      );

      // ScrollTrigger.create({
      //   animation: videoTl,
      //   trigger: videoContainerTrayRef.current,
      //   pin: true,
      //   pinSpacing: true,
      //   start: "top top",
      //   end: `+=${window.innerHeight * videoTl.duration()}`,
      //   scrub: true,
      //   invalidateOnRefresh: true,
      // });

      // setTimeout(() => {
      //   ScrollTrigger.refresh();
      // }, 0);
    }
  }, [videoReady]);

  // Build a product-page link for a curated homepage image, resolving the
  // product by name/family and (optionally) the variation by colour, so the
  // image links straight to the right product and colour
  // (/product/<id>?variation=<index>). Falls back to the shop if not found.
  const buildProductLink = (match, colour) => {
    const m = match.toLowerCase();
    // Prefer an exact name/family match (so "Duo" → the Duo lamp, not "Duo
    // Cable"; "Orb S" → Orb S, not Orb L/XL), then fall back to a contains match.
    const product =
      products?.find(
        (p) => p.name?.toLowerCase() === m || p.family?.toLowerCase() === m,
      ) ||
      products?.find(
        (p) =>
          p.family?.toLowerCase().includes(m) ||
          p.name?.toLowerCase().includes(m),
      );
    if (!product) return "/products/all";
    let variationIndex = 0;
    if (colour) {
      const idx = product.variations.findIndex(
        (v) =>
          v.colour && v.colour.toLowerCase().includes(colour.toLowerCase()),
      );
      if (idx >= 0) variationIndex = idx;
    }
    return `/product/${product.id}?variation=${variationIndex}`;
  };

  return (
    <div>
      <DropdownMenu
        overideVisible={visible}
        showElements={showElements}
        backVisible={false}
      />
      <SEO title="Cork Matters" />
      <div className={`${styles.videoContainer} ${styles.grid}`}>
        <div style={{ opacity: `${opacity}`, gridColumn: "span 12" }}>
          <video
            key={isPortrait ? "portrait" : "landscape"}
            className={styles.fixedVideo}
            src={isPortrait ? mobileHomepageVideo : newShopVideo}
            autoPlay
            loop
            muted
            playsInline
            transition="fade"
          ></video>
        </div>
      </div>
      <div className={styles.hero}>
        <div className={styles.desktop}>
          <header className={styles.grid}>
            <div className={styles.navbar}>
              <div className={styles.linksBar}></div>
            </div>
          </header>
          <div className={styles.logoContainer}>
            <div className={`${styles.grid} ${styles.fullWdith}`}>
              <div className={styles.logo}></div>
              <div
                className={`${styles.buttonContainer}`}
                style={{ opacity: `${opacity}` }}
              >
                <div className="btn-group">
                  <Link
                    to="/products/all"
                    className="btn-icon-link w-inline-block"
                  >
                    <div className="btn-icon-content">
                      <div className="btn-icon-content__mask">
                        <span
                          data-button-anim-target=""
                          className="btn-icon-content__text"
                        >
                          Visit our shop
                        </span>
                      </div>
                      <div data-icon-size="normal" className="btn-icon-icon">
                        <div
                          data-button-anim-target=""
                          className="btn-icon-icon__bg"
                        ></div>
                        <div className="btn-icon-icon__wrap">
                          <div className="btn-icon-icon__list">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              viewBox="0 0 10 8"
                              fill="none"
                              data-button-anim-target=""
                              className="btn-icon-icon__arrow"
                            >
                              <path
                                d="M4.45231 0.385986H6.02531L9.30131 3.99999L6.02531 7.61399H4.45231L7.40331 4.58499H0.695312V3.42799H7.41631L4.45231 0.385986Z"
                                fill="currentColor"
                              ></path>
                            </svg>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              viewBox="0 0 10 8"
                              fill="none"
                              data-button-anim-target=""
                              className="btn-icon-icon__arrow"
                            >
                              <path
                                d="M4.45231 0.385986H6.02531L9.30131 3.99999L6.02531 7.61399H4.45231L7.40331 4.58499H0.695312V3.42799H7.41631L4.45231 0.385986Z"
                                fill="currentColor"
                              ></path>
                            </svg>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              viewBox="0 0 10 8"
                              fill="none"
                              data-button-anim-target=""
                              className="btn-icon-icon__arrow"
                            >
                              <path
                                d="M4.45231 0.385986H6.02531L9.30131 3.99999L6.02531 7.61399H4.45231L7.40331 4.58499H0.695312V3.42799H7.41631L4.45231 0.385986Z"
                                fill="currentColor"
                              ></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div
                        data-button-anim-target=""
                        className="btn-icon-content__bg"
                      ></div>
                    </div>
                  </Link>
                  <Link to="/cork" className="btn-icon-link w-inline-block">
                    <div className="btn-icon-content">
                      <div className="btn-icon-content__mask">
                        <span
                          data-button-anim-target=""
                          className="btn-icon-content__text"
                        >
                          Story of Cork
                        </span>
                      </div>
                      <div data-icon-size="normal" className="btn-icon-icon">
                        <div
                          data-button-anim-target=""
                          className="btn-icon-icon__bg"
                        ></div>
                        <div className="btn-icon-icon__wrap">
                          <div className="btn-icon-icon__list">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              viewBox="0 0 10 8"
                              fill="none"
                              data-button-anim-target=""
                              className="btn-icon-icon__arrow"
                            >
                              <path
                                d="M4.45231 0.385986H6.02531L9.30131 3.99999L6.02531 7.61399H4.45231L7.40331 4.58499H0.695312V3.42799H7.41631L4.45231 0.385986Z"
                                fill="currentColor"
                              ></path>
                            </svg>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              viewBox="0 0 10 8"
                              fill="none"
                              data-button-anim-target=""
                              className="btn-icon-icon__arrow"
                            >
                              <path
                                d="M4.45231 0.385986H6.02531L9.30131 3.99999L6.02531 7.61399H4.45231L7.40331 4.58499H0.695312V3.42799H7.41631L4.45231 0.385986Z"
                                fill="currentColor"
                              ></path>
                            </svg>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              viewBox="0 0 10 8"
                              fill="none"
                              data-button-anim-target=""
                              className="btn-icon-icon__arrow"
                            >
                              <path
                                d="M4.45231 0.385986H6.02531L9.30131 3.99999L6.02531 7.61399H4.45231L7.40331 4.58499H0.695312V3.42799H7.41631L4.45231 0.385986Z"
                                fill="currentColor"
                              ></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div
                        data-button-anim-target=""
                        className="btn-icon-content__bg"
                      ></div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`${styles.homeSection} ${styles.spacer50vh}`}>
        <div className={styles.grid}>
          <div className={`${styles.textSection} `} ref={textSection1}>
            <div className={styles.title}>New Cork Matters® store</div>
            <div className={styles.bottomContent}>
              <div className={styles.mainText}>
                After two years of existence, Cork Matters has opened its new
                store in the center of Porto, at Rua do Rosário 143 - 145, in a
                block surrounded by art galleries, alternative and vintage
                stores, design studios, (Bombarda Quarteirão Criativo). A
                Concept Design Store with a selection of products from the
                collection and limited edition series.
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/products/all"
                  className={`${styles.isAlt} ${styles.underlineLink}`}
                >
                  See the shop
                </Link>
              </div>
            </div>
          </div>
          <div
            className={`${styles.imageParallax} ${styles.longPhoto}`}
            ref={image1}
          >
            <img src={shopPhoto} alt="Blue Orb S" />
          </div>
        </div>
      </div>
      {/* <div
        className={`${styles.videoContainerTray}`}
        ref={videoContainerTrayRef}
      >
        {(() => {
          let videoSrc, videoName;
          if (isSafari) {
            if (isMobile) {
              videoSrc = trayAnimationSafariBV;
              videoName = "clipcolorbowl2V.mov";
            } else {
              videoSrc = trayAnimationSafariB;
              videoName = "bowlcolorfade.mov";
            }
          } else {
            if (isMobile) {
              videoSrc = trayAnimationV;
              videoName = "naturalBowlV2.webm";
            } else {
              videoSrc = trayAnimation;
              videoName = "naturalbowlV07.webm";
            }
          }
          console.log(
            `Selected video: ${videoName} (isSafari: ${isSafari}, isMobile: ${isMobile}, userAgent: ${navigator.userAgent}, vendor: ${navigator.vendor})`
          );
          return (
            <video
              className={styles.fixedVideoTray}
              src={videoSrc}
              muted
              playsInline
              ref={videoRefTray}
              preload="auto"
              style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
            />
          );
        })()}
        <div
          ref={text1Ref}
          className={`${styles.videoOverlayText} ${
            isMobile ? styles.top : styles.left
          }`}
        >
          Cork.
          <p className={styles.videoOverlayBodyText}>
            Natural cork agglomerate is incorporated together to create a
            complex textured experience.
          </p>
        </div>
        <div
          ref={text2Ref}
          className={`${styles.videoOverlayText} ${
            isMobile ? styles.bottom : styles.right
          }`}
        >
          Longevity.
          <p className={styles.videoOverlayBodyText}>
            You can clean cork with just water and all Cork Matters products
            come with UV protection.
          </p>
        </div>
      </div> */}
      <div className={`${styles.homeSection}`}>
        <div className={styles.grid} ref={imageSectionRef}>
          <Link
            to={buildProductLink("Orb L", "Natural")}
            className={`${styles.squarePhoto} ${styles.span3} ${styles.hiddenMobile}`}
          >
            <img src={OrbS1} />
          </Link>
          <Link
            to={buildProductLink("Orb L", "Terracotta")}
            className={`${styles.squarePhoto} ${styles.span3} ${styles.hiddenMobile}`}
          >
            <img src={OrbS2} />
          </Link>
          <Link
            to={buildProductLink("Orb XL", "Terracotta")}
            className={`${styles.squarePhoto} ${styles.span3}`}
          >
            <img src={terracottaOrbs} />
          </Link>
          <Link
            to={buildProductLink("Orb L", "Blue")}
            className={`${styles.squarePhoto} ${styles.span3} ${styles.hiddenMobile}`}
          >
            <img src={OrbS4} />
          </Link>
          <Link
            to={buildProductLink("Orb S", "Natural")}
            className={`${styles.squarePhoto} ${styles.span4}`}
          >
            <img src={orbShelf} />
          </Link>
          <Link
            to={buildProductLink("Duo", "Blue")}
            className={`${styles.squarePhoto} ${styles.span4} ${styles.hiddenMobile}`}
          >
            <img src={Duo2} />
          </Link>
          <Link
            to={buildProductLink("Duo", "Natural")}
            className={`${styles.squarePhoto} ${styles.span4} ${styles.hiddenMobile}`}
          >
            <img src={Duo3} />
          </Link>
          {/* <div className={`${styles.squarePhoto} ${styles.span6}`}>
            <img src={trayTallBlue} />
          </div> */}
          <Link
            to={buildProductLink("Loop", "Natural")}
            className={`${styles.squarePhoto} ${styles.span6}`}
          >
            <img src={trayNaturalStool} />
          </Link>
          {/* <div className={`${styles.squarePhoto} ${styles.span6}`}>
            <img src={woodenFloorOrbXL} />
          </div> */}
          <Link
            to={buildProductLink("Tray Round", "Natural")}
            className={`${styles.squarePhoto} ${styles.span6}`}
          >
            <img src={togoHouses} />
          </Link>
          <div
            className={`${styles.textSection} ${styles.long} ${styles.blue} ${styles.noMobileReorder}`}
            ref={textSection1}
          >
            <div className={styles.title}>About us</div>
            <div className={styles.bottomContent}>
              <div className={styles.text4}>
                Cork Matters® is here to revolutionize your relationship with
                sustainable design. Founded by Porto based designers Alzira
                Peixoto and Carlos Mendonça, we transform Portugal’s finest cork
                into award-winning products that blend timeless craftsmanship
                with cutting-edge innovation. Our eco-friendly creations—ranging
                from our home collection to bespoke products harness cork’s
                natural durability, texture, and sustainability, enhancing
                spaces with both beauty and purpose. Discover Portugal’s most
                celebrated cork designs, delivering exceptional functionality
                and a greener future, every time.
              </div>
            </div>
          </div>
          <div className={`${styles.longPhoto} ${styles.span6}`}>
            <img src={verticalAmbient} />
          </div>
          <div className={`${styles.longPhoto} ${styles.span6}`}>
            <video src={greenBowlBooks} autoPlay loop muted playsInline></video>
          </div>
          <div
            className={`${styles.textSection} ${styles.long} ${styles.darkGreen} ${styles.noMobileReorder}`}
            ref={textSection1}
          >
            <div className={styles.title}>Bowl Large Spring Green</div>
            <div className={styles.bottomContent}>
              <div className={styles.text4}>
                A generous cork bowl designed for holding everyday essentials,
                merging practicality with quiet sophistication. Cork is
                renewable and biodegradable, and using agglomerate utilises
                waste from the cork industry, making the process more
                environmentally friendly. What's more, cork's texture and sage
                green tone offer a modern, natural aesthetic that fits
                seamlessly into diverse spaces, with its flexibility enabling
                tailored, intuitive arrangements, starting in the home and
                finishing in an office space.
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/products/all"
                  className={`${styles.isAlt} ${styles.underlineLink} ${styles.white}`}
                >
                  See the collection
                </Link>
              </div>
            </div>
          </div>
          <div
            className={`${styles.textSection} ${styles.long} ${styles.terracotta}`}
            ref={textSection1}
          >
            <div className={styles.title}>
              Orb S <div className={styles.colour}>Terracotta </div>
            </div>
            <div className={styles.bottomContent}>
              <div className={styles.mainText}>
                A spherical cork lamp designed to be either ceiling mounted or
                placed securely a support, combining sustainability with
                contemporary design. Cork is renewable and biodegradable, and
                using agglomerate utilises waste of the cork industry, making
                the process more environmentally friendly. What's more, cork's
                texture and colour offer a modern, natural look that adapts to
                different styles, and its versatility makes it possible to
                create personalised, creative designs.
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/products/all"
                  className={`${styles.isAlt} ${styles.underlineLink} ${styles.white}`}
                >
                  See the collection
                </Link>
              </div>
            </div>
          </div>
          <div className={`${styles.longPhoto} ${styles.span6}`}>
            <video
              src={teracottaLampHands}
              autoPlay
              loop
              muted
              playsInline
            ></video>
          </div>
        </div>
      </div>
      <div className={`${styles.homeSection}`}>
        <div className={styles.grid}>
          <div
            className={`${styles.landscapePhoto} ${styles.span12} ${styles.hiddenMobile}`}
          >
            <img src={houses} />
          </div>
        </div>
      </div>
      <div className={styles.homeSection}>
        <div className={styles.grid} ref={section4}>
          <div className={styles.imageLeft}>
            {products
              .filter((product) => product.name.includes("Orb L"))
              .map((product) => (
                <div className={styles.imageContainer} key={product.id}>
                  <Link
                    to={`/product/${product.id}`}
                    className={styles.productLinkHome}
                  >
                    <img src={product.variations[0].imageSrc[0]} />
                    <p className={styles.productTitleHome}>
                      {product.family
                        ? product.family.toUpperCase()
                        : product.name.toUpperCase()}{" "}
                      {product.family ? product.size.toUpperCase() : ""}
                    </p>
                  </Link>
                </div>
              ))}
          </div>
          <div className={styles.textSectionRight} ref={textSection4}>
            <div className={styles.mainText}>
              Cork is a natural material of great importance in our modern
              world. Its sustainability, renewability and recyclability make it
              an unusually environmentally friendly choice. Its insulation
              properties, versatility and health benefits also increase its
              value in various industries. By recognising the importance of cork
              and choosing cork-based products, we can contribute to a more
              sustainable and environmentally friendly future, while enjoying
              the countless benefits this remarkable material has to offer.
            </div>
            <div className={styles.bottomContent}>
              <div className={styles.linkContainer}>
                <Link
                  to="/products/all"
                  className={`${styles.isAlt} ${styles.underlineLink}`}
                >
                  See the Cork Matters shop
                </Link>
              </div>
            </div>
          </div>
          <div className={styles.imageLeft}>
            {products
              .filter((product) => product.name.includes("Orb XL"))
              .map((product) => (
                <div className={styles.imageContainer} key={product.id}>
                  <Link
                    to={`/product/${product.id}`}
                    className={styles.productLinkHome}
                  >
                    <img src={OrbXLNat} alt="Cork Trees" />
                    <p className={styles.productTitleHome}>
                      {product.family
                        ? product.family.toUpperCase()
                        : product.name.toUpperCase()}{" "}
                      {product.family ? product.size.toUpperCase() : ""}
                    </p>
                  </Link>
                </div>
              ))}
          </div>
          <div className={styles.imageRight}>
            {products
              .filter((product) => product.name.includes("Notebook"))
              .map((product) => (
                <div className={styles.imageContainer} key={product.id}>
                  <Link
                    to={`/product/${product.id}`}
                    className={styles.productLinkHome}
                  >
                    <img
                      src={product.variations[0].imageSrc[0]}
                      alt="Cork Trees"
                    />
                    <p className={styles.productTitleHome}>
                      {product.family
                        ? product.family.toUpperCase()
                        : product.name.toUpperCase()}{" "}
                      {product.family ? product.size.toUpperCase() : ""}
                    </p>
                  </Link>
                </div>
              ))}
          </div>
        </div>
      </div>
      <FAQ />
      <Footer />
    </div>
  );
}

export default Home;
